import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Pencil, XCircle, CheckCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import apiClient, { get } from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import Modal from '../../components/common/Modal.jsx';

const TIPOS = ['Admin'];

/**
 * Tabla "Telefonos Registrados" -- en V1 vive en la MISMA pantalla WhatsApp
 * (`IbuFront/src/components/views/WhatsApp/Whatsapp.jsx`, columna derecha), no es una
 * pantalla aparte: este componente se incrusta en `WhatsAppPanelPage.jsx` (ya no tiene
 * ruta propia ni entrada en el menú, igual que V1).
 *
 * Réplica de V1: botón "Agregar", tabla Nombre / Telefono / Tipo / Editar (botón
 * editar + botón baja/alta por fila, con confirmación "Estas seguro?"), ordenada por
 * nombre; modales "Editar Telefono" / "Agregar Telefono" con Nombre, Telefono, Tipo
 * (solo "Admin"). Se quitó lo que V1 NO tiene: columna "Estatus" y el botón "Eliminar"
 * (borrado físico).
 *
 * Endpoints: `IbuBack2/src/api/v1/modules/whatsapp/telefonos-whatsapp.routes.js`
 *   GET /telefonos-whatsapp, POST /telefonos-whatsapp, PATCH /telefonos-whatsapp/:id
 */
export default function TelefonosRegistrados() {
  const { isAdmin } = useContext(AuthContext);

  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [tipo, setTipo] = useState(TIPOS[0]);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const data = await get('/telefonos-whatsapp');
      setFilas(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
      setFilas([]);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin()) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargar]);

  function abrirNuevo() {
    setEditando(null);
    setNombre('');
    setTelefono('');
    setTipo(TIPOS[0]);
    setFormOpen(true);
  }

  function abrirEditar(row) {
    setEditando(row);
    setNombre(row.name || '');
    setTelefono(row.telefono ?? '');
    setTipo(row.tipo || TIPOS[0]);
    setFormOpen(true);
  }

  async function guardar(e) {
    e.preventDefault();
    if (!nombre.trim() || !telefono || !tipo) {
      Swal.fire({ icon: 'warning', title: 'Completa nombre, teléfono y tipo' });
      return;
    }
    setGuardando(true);
    const payload = { name: nombre.trim(), telefono: Number(telefono), tipo };
    try {
      if (editando) await apiClient.patch(`/telefonos-whatsapp/${editando._id}`, payload);
      else await apiClient.post('/telefonos-whatsapp', payload);
      setFormOpen(false);
      Swal.fire({ icon: 'success', title: 'Actualizado con exito', timer: 1500, showConfirmButton: false });
      await cargar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: err.message });
    } finally {
      setGuardando(false);
    }
  }

  // Misma confirmación que `Baja.jsx` de V1.
  async function alternarActivo(row) {
    const baja = row.is_active !== 'No';
    const r = await Swal.fire({
      title: 'Estas seguro?',
      text: baja ? 'Se dara de baja!' : 'Se dara de Alta!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: baja ? 'Si, dar de Baja!' : 'Si, dar de Alta!',
    });
    if (!r.isConfirmed) return;
    try {
      await apiClient.patch(`/telefonos-whatsapp/${row._id}`, { is_active: baja ? 'No' : 'Si' });
      Swal.fire({ position: 'center', icon: 'success', title: baja ? 'Se dio de Baja' : 'Se dio de Alta', showConfirmButton: false, timer: 2000 });
      await cargar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Something went wrong!', footer: err.message });
    }
  }

  if (!isAdmin()) return null;

  const ordenadas = [...filas].sort((a, b) => (a.name > b.name ? 1 : -1));

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-900">Telefonos Registrados</h2>
      <button type="button" onClick={abrirNuevo} className="btn-danger text-xs px-3 py-1.5">Agregar</button>
      {error && <p className="text-sm text-danger-600">{error}</p>}

      <div className="card overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-600">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Nombre</th>
              <th className="px-4 py-3 text-left font-semibold">Telefono</th>
              <th className="px-4 py-3 text-left font-semibold">Tipo</th>
              <th className="px-4 py-3 text-left font-semibold">Editar</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {cargando && <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">Cargando...</td></tr>}
            {!cargando && ordenadas.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-slate-400">Sin registros</td></tr>
            )}
            {!cargando && ordenadas.map((row) => (
              <tr key={row._id} className={row.is_active === 'No' ? 'bg-danger-50' : ''}>
                <td className="px-4 py-2">{row.name}</td>
                <td className="px-4 py-2">{row.telefono}</td>
                <td className="px-4 py-2">{row.tipo}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1">
                    <button title="Editar" aria-label="Editar" className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50" onClick={() => abrirEditar(row)}>
                      <Pencil size={15} />
                    </button>
                    {row.is_active === 'No' ? (
                      <button title="Dar de alta" aria-label="Dar de alta" className="p-1.5 rounded-md text-success-600 hover:bg-slate-100" onClick={() => alternarActivo(row)}>
                        <CheckCircle size={15} />
                      </button>
                    ) : (
                      <button title="Dar de baja" aria-label="Dar de baja" className="p-1.5 rounded-md text-danger-500 hover:bg-slate-100" onClick={() => alternarActivo(row)}>
                        <XCircle size={15} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={formOpen} onClose={() => setFormOpen(false)} title={editando ? 'Editar Telefono' : 'Agregar Telefono'} width="max-w-sm"
        footer={(
          <button type="submit" form="form-telefono-whatsapp" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando...' : 'Guardar'}
          </button>
        )}
      >
        <form id="form-telefono-whatsapp" onSubmit={guardar} className="space-y-4">
          <div>
            <label className="label">Nombre</label>
            <input value={nombre} onChange={(e) => setNombre(e.target.value)} className="input-base w-full" />
          </div>
          <div>
            <label className="label">Telefono</label>
            <input type="number" value={telefono} onChange={(e) => setTelefono(e.target.value)} className="input-base w-full" />
          </div>
          <div>
            <label className="label">Tipo</label>
            <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="select-base w-full">
              {TIPOS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
